import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useProject } from '@/contexts/ProjectContext';
import api from '@/lib/api';

const PermissionContext = createContext(null);

/**
 * Provides project-level RBAC permission checking.
 * Fetches the user's sidebar (which includes access levels) for the selected project.
 */
export function PermissionProvider({ children }) {
  const { user } = useAuth();
  const { projectId } = useProject();
  const [permissions, setPermissions] = useState({});
  const [membership, setMembership] = useState(null);

  const fetchPermissions = useCallback(async () => {
    if (!user || !projectId) {
      setPermissions({});
      return;
    }
    try {
      // Fetch the user's membership for this project from their profile
      const meRes = await api.get('/auth/me');
      const platformRole = meRes.data?.platformRole || meRes.data?.role;
      if (['SUPERADMIN', 'PLATFORM_ADMIN', 'admin'].includes(platformRole)) {
        // God mode: a stored project grant never limits the platform owner.
        setMembership(null);
        setPermissions({ _isAdmin: true });
        return;
      }
      const projectAccess = JSON.parse(localStorage.getItem('ciq_project_access') || '[]');
      const myMembership = projectAccess.find(m => m.projectId === projectId);

      // Fetch full member detail with module permissions
      const memberRes = await api.get(`/projects/${projectId}/members/${user.user_id || user.id}`).catch(() => null);
      if (memberRes?.data?.membership) {
        const mbr = memberRes.data.membership;
        setMembership(mbr);
        const permMap = {};
        for (const mp of mbr.modulePermissions || []) {
          permMap[mp.moduleCode] = {
            accessLevel: mp.accessLevel,
            ...mp.actions
          };
        }
        setPermissions(permMap);
      } else {
        // Platform admin or legacy user — grant all
        const role = meRes.data?.platformRole || meRes.data?.role;
        if (role === 'PLATFORM_ADMIN' || role === 'admin') {
          setPermissions({ _isAdmin: true });
        }
      }
    } catch {
      setPermissions({});
    }
  }, [user, projectId]);

  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  /**
   * Check if the current user can perform an action on a module.
   * @param {string} moduleCode - e.g. 'HSE', 'RAID', 'COST_CONTROL'
   * @param {string} action - 'view' | 'create' | 'edit' | 'delete' | 'approve' | 'export' | 'import' | 'configure' | 'admin'
   * @returns {boolean}
   */
  const can = useCallback((moduleCode, action = 'view') => {
    if (permissions._isAdmin) return true;
    const mod = permissions[moduleCode];
    if (!mod) return true; // If no permission data loaded yet, don't block (fail-open on frontend)
    if (mod.accessLevel === 'NO_ACCESS') return false;
    // Explicit action flag wins
    if (Object.prototype.hasOwnProperty.call(mod, action)) return mod[action] ?? false;
    // Fall back to accessLevel-derived defaults when the action key is missing
    // (e.g. modulePermissions may only carry view/create/edit/delete/approve,
    //  but the UI asks about import/export/configure/admin — derive from level).
    const LEVEL_ACTIONS = {
      ADMIN:       { view: true, create: true, edit: true, delete: true, approve: true, export: true, import: true, configure: true, admin: true },
      MANAGER:     { view: true, create: true, edit: true, delete: false, approve: true, export: true, import: true, configure: false, admin: false },
      CONTRIBUTOR: { view: true, create: true, edit: true, delete: false, approve: false, export: true, import: false, configure: false, admin: false },
      VIEW_ONLY:   { view: true, create: false, edit: false, delete: false, approve: false, export: true, import: false, configure: false, admin: false },
    };
    const fallback = LEVEL_ACTIONS[mod.accessLevel];
    return fallback ? (fallback[action] ?? false) : false;
  }, [permissions]);

  /**
   * Get the access level for a module.
   * @returns {'ADMIN' | 'MANAGER' | 'CONTRIBUTOR' | 'VIEW_ONLY' | 'NO_ACCESS' | null}
   */
  const getAccessLevel = useCallback((moduleCode) => {
    if (permissions._isAdmin) return 'ADMIN';
    return permissions[moduleCode]?.accessLevel || null;
  }, [permissions]);

  /**
   * Check if user has at least view access to a module.
   */
  const canView = useCallback((moduleCode) => can(moduleCode, 'view'), [can]);
  const canCreate = useCallback((moduleCode) => can(moduleCode, 'create'), [can]);
  const canEdit = useCallback((moduleCode) => can(moduleCode, 'edit'), [can]);
  const canDelete = useCallback((moduleCode) => can(moduleCode, 'delete'), [can]);
  const canApprove = useCallback((moduleCode) => can(moduleCode, 'approve'), [can]);
  const canExport = useCallback((moduleCode) => can(moduleCode, 'export'), [can]);
  const canImport = useCallback((moduleCode) => can(moduleCode, 'import'), [can]);

  return (
    <PermissionContext.Provider value={{
      permissions, membership, can,
      getAccessLevel, canView, canCreate, canEdit, canDelete, canApprove, canExport, canImport,
      refreshPermissions: fetchPermissions,
    }}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermissions must be inside PermissionProvider');
  return ctx;
}
